import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { HpIcon, Icon, type IconName } from './icons.js';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// --- actions ---

export type HpButtonVariant = 'primary' | 'secondary' | 'quiet' | 'icon' | 'on-dark' | 'ghost-dark';

export interface HpButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: HpButtonVariant;
  /** Leading 16 px icon (an icon-only button needs `aria-label`). */
  icon?: IconName;
  /**
   * The action this button triggered is in flight: the button is disabled, announces `aria-busy`
   * and swaps its icon for the pulsing running dot while keeping its label and colours, so the
   * click is acknowledged the moment it happens. Pass the mutation's `isPending` here and keep
   * `disabled` for every other reason (missing permission, invalid form, a sibling's pending call).
   */
  busy?: boolean;
}

/** `.hp-button.hp-button--<variant>` — 38 px, 12 px/700, radius 9, no movement on hover. */
export function HpButton({
  variant = 'quiet',
  icon,
  busy = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: HpButtonProps) {
  return (
    <button
      type={type}
      className={cx('hp-button', `hp-button--${variant}`, busy && 'is-busy', className)}
      disabled={busy || disabled}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="hp-button__busy" aria-hidden="true" /> : icon && <HpIcon name={icon} />}
      {children !== undefined && children !== null && <span>{children}</span>}
    </button>
  );
}

export interface DetailActionProps {
  /** With `href` renders an anchor, otherwise a button. */
  href?: string;
  onClick?: () => void;
  label?: ReactNode;
  ariaLabel?: string;
  /** Text variant: no background, underline on hover. */
  text?: boolean;
  className?: string;
  target?: string;
  rel?: string;
}

/** `.hp-detail-action` — the one "Detail ↗" action for lists, related records and units. */
export function DetailAction({
  href,
  onClick,
  label = 'Detail',
  ariaLabel,
  text,
  className,
  target,
  rel,
}: DetailActionProps) {
  const cls = cx('hp-detail-action', text && 'hp-detail-action--text', className);
  const inner = (
    <>
      {label} <HpIcon name="arrowUpRight" />
    </>
  );
  if (href) {
    return (
      <a className={cls} href={href} aria-label={ariaLabel} target={target} rel={rel} onClick={onClick}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" className={cls} aria-label={ariaLabel} onClick={onClick}>
      {inner}
    </button>
  );
}

export interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: IconName;
  iconAfter?: IconName;
}

/** `.text-button` — 13 px muted text action. */
export function TextButton({ icon, iconAfter, className, children, type = 'button', ...rest }: TextButtonProps) {
  return (
    <button type={type} className={cx('text-button', className)} {...rest}>
      {icon && <Icon name={icon} />}
      <span>{children}</span>
      {iconAfter && <Icon name={iconAfter} />}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Required: an icon-only control must name itself. */
  'aria-label': string;
  /** Its own request is in flight: disabled, `aria-busy`, the icon becomes the pulsing dot (see `HpButton`). */
  busy?: boolean;
}

/** `.icon-button` — 34 × 34 px icon-only control. */
export function IconButton({ icon, busy = false, disabled, className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      className={cx('icon-button', busy && 'is-busy', className)}
      disabled={busy || disabled}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="hp-button__busy" aria-hidden="true" /> : <Icon name={icon} />}
    </button>
  );
}

// --- labels and states ---

export type BadgeTone = 'green' | 'amber' | 'blue' | 'purple' | 'gray';

/** `.badge.<tone>` — soft pill (30 px inside #main tables). Tone comes from a status mapping, never chosen by hand. */
export function Badge({ tone, children, className }: { tone: BadgeTone; children: ReactNode; className?: string }) {
  return <span className={cx('badge', tone, className)}>{children}</span>;
}

export type HpTagTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

/** `.hp-tag.hp-tag--<tone>` — system pill with a 5 px dot. */
export function HpTag({
  tone = 'neutral',
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: HpTagTone;
  children: ReactNode;
}) {
  return (
    <span className={cx('hp-tag', `hp-tag--${tone}`, className)} {...rest}>
      {children}
    </span>
  );
}

export type HpStatusTone = 'success' | 'warning' | 'danger' | 'neutral';

/** `.hp-status.hp-status--<tone>` — compact dot + text state that does not compete with pills. */
export function HpStatus({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: HpStatusTone;
  children: ReactNode;
  className?: string;
}) {
  return <span className={cx('hp-status', tone !== 'neutral' && `hp-status--${tone}`, className)}>{children}</span>;
}

export type AvatarColor = 'navy' | 'peach' | 'lavender' | 'green';

/** First letters of at most two words; "HP" when the name is empty. */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const initials = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
  return initials || 'HP';
}

/** `.avatar(.color)` — 29 px initials disc. */
export function Avatar({
  name,
  color,
  className,
}: {
  name: string | null | undefined;
  color?: AvatarColor;
  className?: string;
}) {
  return (
    <span className={cx('avatar', color, className)} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

/** `.owner` — avatar + short name. */
export function OwnerChip({ name, color }: { name: string | null | undefined; color?: AvatarColor }) {
  return (
    <span className="owner">
      <Avatar name={name} color={color} />
      <span>{name ?? '—'}</span>
    </span>
  );
}

/** `.scope-tag(.public)` — public/internal marker. */
export function ScopeTag({ scope, children }: { scope: 'public' | 'internal'; children?: ReactNode }) {
  return (
    <span className={cx('scope-tag', scope === 'public' && 'public')}>
      <Icon name={scope === 'public' ? 'globe' : 'lock'} />
      {children ?? (scope === 'public' ? 'Public' : 'Internal')}
    </span>
  );
}

// --- content atoms ---

export interface FieldProps {
  label: ReactNode;
  value: ReactNode;
  /** Shown when `value` is null, undefined or an empty string. */
  emptyLabel?: string;
  full?: boolean;
}

/** `.field` label + value; empty values read "Not filled in yet" (the prototype's „Zatím nevyplněno“). */
export function Field({ label, value, emptyLabel = 'Not filled in yet', full }: FieldProps) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={cx('field', full && 'full')}>
      <span className="field-label">{label}</span>
      <div className={cx('field-value', empty && 'missing')}>{empty ? emptyLabel : value}</div>
    </div>
  );
}

/** `.field-grid` — two-column field layout inside a panel. */
export function FieldGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('field-grid', className)}>{children}</div>;
}

/** `.empty-state` — symbol, title, one sentence, optional action. */
export function EmptyState({
  icon = 'search',
  title,
  children,
  action,
}: {
  icon?: IconName;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-symbol">
        <Icon name={icon} />
      </span>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/** `.note(.amber|.green)` — quiet inline remark inside a panel. */
export function Note({
  tone,
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { tone?: 'amber' | 'green' }) {
  return (
    <div className={cx('note', tone, className)} {...rest}>
      {children}
    </div>
  );
}

/** `kbd` — keyboard hint. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd>{children}</kbd>;
}

export type { AnchorHTMLAttributes };
